import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import Select from "@/components/Select";
import useApi from "@/hooks/useApi";
import React, { useMemo, useState, useEffect } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { IVRVoiceConfiguration } from "../types";

import { PrimaryButton } from "@/components/ds";

interface Props {
  onClose(): void;
  onSuccess(): void;
}

interface PhoneNumber {
  friendly_name: string;
  phone_number: string;
}

const errorMap: Record<string, string> = {
  sms_error: "An error occured while purchasing.",
  number_not_found: "No phone number found for the selected area code.",
  invalid_area_code: "Invalid area code."
};

export default function IvrIntegrationSetupModal(props: Props) {
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [error, setError] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [step, setStep] = useState<"lookup" | "purchase">("lookup");
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[] | null>(null);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [voiceConfigurations, setVoiceConfigurations] = useState<
    IVRVoiceConfiguration[]
  >([]);
  const [selectedVoiceConfig, setSelectedVoiceConfig] = useState<
    number | undefined
  >(undefined);

  const api = useApi();

  useEffect(() => {
    fetchVoiceConfigurations();
  }, []);

  async function fetchVoiceConfigurations() {
    try {
      const response = await api.messaging.get("IVRVoiceConfigurations");
      const data: IVRVoiceConfiguration[] = await response.json();
      setVoiceConfigurations(data);

      // Auto-select first available if none selected
      if (!selectedVoiceConfig && data.length > 0) {
        setSelectedVoiceConfig(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching voice configurations:", error);
    }
  }

  const buttonText = useMemo(() => {
    switch (true) {
      case step == "lookup" && !loading:
        return "Find phone number";
      case step == "lookup" && loading:
        return "Finding numbers...";
      case step == "purchase" && !loading:
        return "Purchase";
      case step == "purchase" && loading:
        return "Purchasing...";
    }
  }, [loading, step]);

  const phoneNumberOptions = useMemo(() => {
    const options: Record<string, string> = {};
    if (!Array.isArray(phoneNumbers)) {
      return options;
    }
    for (const num of phoneNumbers) {
      const key =
        typeof num.phone_number === "string"
          ? num.phone_number
          : String(num.phone_number ?? "");
      const label =
        typeof num.friendly_name === "string" ? num.friendly_name : key;
      if (key) options[key] = label;
    }
    return options;
  }, [phoneNumbers]);

  const fetchAvailablePhoneNumbers = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.messaging.post(
        "Sms/Integrations/GetPhoneNumbers",
        {
          countryCode: selectedCountry,
          areaCode: areaCode
        }
      );

      if (!response.ok) {
        const errKey: string = await response.json().catch(() => "sms_error");
        setError(errorMap[errKey] ?? errorMap["sms_error"]);
        setLoading(false);
        return;
      }

      const raw: unknown = await response.json();
      const numbers: PhoneNumber[] = Array.isArray(raw)
        ? (raw as PhoneNumber[])
        : [];

      if (numbers.length === 0) {
        setError(errorMap["number_not_found"]);
      } else {
        const firstPhone = numbers[0].phone_number;
        setSelectedNumber(typeof firstPhone === "string" ? firstPhone : "");
      }
      setPhoneNumbers(numbers);
      setStep("purchase");
    } catch (e) {
      console.error("[IvrIntegrationSetupModal] phone number lookup failed", e);
      setError(errorMap["sms_error"]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, areaCode, selectedCountry]);

  async function submitPurchase() {
    setError("");
    setLoading(true);
    try {
      const resp = await api.messaging.post("Sms/Integrations", {
        phoneNumber: selectedNumber,
        teamId: selectedTeam,
        isIvr: true,
        ivrVoiceConfigurationId: selectedVoiceConfig
      });
      setSuccess(resp.ok);
      if (resp.ok) {
        props.onSuccess();
      } else {
        const error: string = await resp.json();
        setError(errorMap[error]);
      }
    } catch (e) {
      setError(errorMap["sms_error"]);
      console.error(e);
      setLoading(false);
    }
  }

  const isValidAreaCode = (code: string): boolean => {
    const areaCodeRegex = /^\d{3}$/;
    return areaCodeRegex.test(code);
  };

  function handleAreaCode(code: string) {
    setAreaCode(code);
    setStep("lookup");
  }

  return (
    <Modal show={true} maxWidth="max-w-[900px]">
      <ModalHeader
        title="Purchase Phone Number"
        onClose={() => props.onClose()}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (step == "lookup") {
            if (isValidAreaCode(areaCode)) {
              setError("");
              fetchAvailablePhoneNumbers();
            } else {
              setError(errorMap["invalid_area_code"]);
            }
          } else {
            submitPurchase();
          }
        }}
      >
        <ModalBody>
          <div className="p-6 max-h-[70vh] overflow-auto">
            {/* <div className="mb-3">
               <label htmlFor="sms-country" className="block mb-1">
                 Country
               </label>
               <CountrySelect
                 setSelectedCountry={setSelectedCountry}
                 value={selectedCountry}
               />
             </div> */}
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Team</div>
              <TeamSelectDeprecated
                id="ar-team"
                className="input w-2/3"
                onChange={(e) => setSelectedTeam(e.currentTarget.value)}
                value={selectedTeam}
              />
            </div>
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Area Code</div>
              <input
                className="input w-2/3"
                onChange={(e) => handleAreaCode(e.target.value)}
                placeholder="Enter area code"
                maxLength={3}
                value={areaCode}
              />
            </div>
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Voice Configuration</div>
              {voiceConfigurations.length === 0 ? (
                <div className="w-2/3 text-zinc-500">
                  No voice configurations available. Please create one first.
                </div>
              ) : (
                <select
                  id="voice-config"
                  className="input w-2/3"
                  onChange={(e) =>
                    setSelectedVoiceConfig(
                      e.currentTarget.value
                        ? Number(e.currentTarget.value)
                        : undefined
                    )
                  }
                  value={selectedVoiceConfig || ""}
                >
                  <option value="">Select Voice Configuration</option>
                  {voiceConfigurations.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {step === "purchase" && (
              <div className="flex mb-4">
                <div className="w-1/3 font-semibold">Phone Numbers</div>
                <Select
                  className="input w-2/3"
                  onChange={(value) => setSelectedNumber(value)}
                  value={selectedNumber}
                  options={phoneNumberOptions}
                />
              </div>
            )}
            <div className="text-crimson-red-500 text-sm mt-2">{error}</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="p-4 flex flex-col mt-4">
            {success === false && (
              <div className="flex flex-row mb-4">
                There was an error saving your changes. Please see Integration
                Log for more details.
              </div>
            )}
            {loading ? (
              <div className="ml-auto flex">
                Saving Changes...
                <LoadingSpinner />
              </div>
            ) : (
              <div className="ml-auto flex flex-row">
                <PrimaryButton type="submit">{buttonText}</PrimaryButton>
              </div>
            )}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
